import { Action, Selector, State, StateContext, Store } from '@ngxs/store'
import { inject, Injectable } from '@angular/core'
import { ResetEvent, UpdateEventField } from './event.actions'
import { BaseEvent, SrEvent } from '../../models/sr-event.model'
import { FORM_FIELDS } from '../../features/event/constants/event-editor.constants'

export interface EventStateModel {
  readonly base: BaseEvent
  readonly 'sr-event': SrEvent
  readonly isFormValid: boolean
  readonly isFormDirty: boolean
  readonly lastUpdated: number
}

@State<EventStateModel>({
  name: 'event',
  defaults: {
    base: {
      workers: [],
      clients: [],
      plannedFrom: null,
      plannedTo: null,
      realizedFrom: null,
      realizedTo: null,
      rrule: '',
      recurrenceException: null,
      recurrenceParentId: null,
      recurrenceEnd: null,
      statusId: 1,
      allocationId: 0,
    },
    'sr-event': {
      title: '',
      typeOfContact: 'phone',
      description: '',
    },
    isFormValid: false,
    isFormDirty: false,
    lastUpdated: 0,
  },
})
@Injectable()
export class EventState {
  private store = inject(Store)

  @Selector()
  static event(state: EventStateModel): EventStateModel {
    return state
  }

  @Selector()
  static baseData(state: EventStateModel): BaseEvent {
    return state.base
  }

  @Selector()
  static srEventData(state: EventStateModel): SrEvent {
    return state['sr-event']
  }

  @Selector()
  static selectClients(state: EventStateModel): ReadonlyArray<{ id: number; label: string }> {
    return (state.base?.clients as any) || []
  }

  @Selector()
  static selectWorkers(state: EventStateModel): ReadonlyArray<{ id: number; label: string }> {
    return (state.base?.workers as any) || []
  }

  @Selector()
  static isFormValid(state: EventStateModel): boolean {
    return state.isFormValid
  }

  @Selector()
  static isFormDirty(state: EventStateModel): boolean {
    return state.isFormDirty
  }

  @Action(UpdateEventField)
  updateEventField(ctx: StateContext<EventStateModel>, action: UpdateEventField): void {
    const currentState = ctx.getState()
    const { section, field, value } = action

    let finalValue = value
    if (field === FORM_FIELDS.ALLOCATION_ID && (value === null || value === undefined || value === 0)) {
      const user = this.store.selectSnapshot(
        (state: Record<string, unknown>) => (state.user as Record<string, unknown>)?.activeAllocationCurrentUser,
      )
      finalValue = (user as { allocationId?: number })?.allocationId ?? 1
    }

    const updatedState = {
      ...currentState,
      [section]: {
        ...currentState[section],
        [field]: finalValue,
      },
      lastUpdated: Date.now(),
    }

    ctx.patchState(updatedState)
  }

  @Action(ResetEvent)
  resetEvent(ctx: StateContext<EventStateModel>): void {
    const user = this.store.selectSnapshot(
      (state: Record<string, unknown>) => (state.user as Record<string, unknown>)?.activeAllocationCurrentUser,
    )
    const allocationId = (user as { allocationId?: number })?.allocationId ?? 0

    ctx.setState({
      base: {
        workers: [],
        clients: [],
        plannedFrom: null,
        plannedTo: null,
        realizedFrom: null,
        realizedTo: null,
        rrule: '',
        recurrenceException: null,
        recurrenceParentId: null,
        recurrenceEnd: null,
        statusId: 1,
        allocationId,
      },
      'sr-event': {
        title: '',
        typeOfContact: 'phone',
        description: '',
      },
      isFormValid: false,
      isFormDirty: false,
      lastUpdated: Date.now(),
    })
  }
}
